// components/Shared/ServerInfoCard.jsx
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useServerConnection } from '../../contexts/ServerConnectionContext';
import { parseMinecraftFormatting } from '../../utils/minecraftFormatting';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNotification } from '../../contexts/NotificationContext';
import axios from 'axios';
import config from '../../config/appConfig';
import { formatTimeAgo } from '../../utils/timeUtils';
import { playVoice } from '../../utils/voicePlayer';

const PAGE_THEMES = {
  home: { bg: '#ffffff', text: '#000000', hoverBg: '#ececee', shadow: 'rgba(255, 255, 255, 0.3)' },
  zeroday: { bg: '#ffffff', text: '#000000', hoverBg: '#ececee', shadow: 'rgba(255, 255, 255, 0.4)' },
  rcon: { bg: '#c026d3', text: '#000000', hoverBg: '#d946ef', shadow: 'rgba(192, 38, 211, 0.45)' },
  ddos: { bg: '#dc2626', text: '#000000', hoverBg: '#ef4444', shadow: 'rgba(220, 38, 38, 0.45)' },
  bots: { bg: '#2563eb', text: '#000000', hoverBg: '#3b82f6', shadow: 'rgba(37, 99, 235, 0.45)' },
  data: { bg: '#14b8a6', text: '#000000', hoverBg: '#2dd4bf', shadow: 'rgba(20, 184, 166, 0.45)' },
  checker: { bg: '#1bd96a', text: '#000000', hoverBg: '#22c55e', shadow: 'rgba(27, 217, 106, 0.45)' },
  servers: { bg: '#0ea5e9', text: '#000000', hoverBg: '#38bdf8', shadow: 'rgba(14, 165, 233, 0.45)' },
  parser: { bg: '#f97316', text: '#000000', hoverBg: '#fb923c', shadow: 'rgba(249, 115, 22, 0.45)' },
  account: { bg: '#ffffff', text: '#000000', hoverBg: '#ececee', shadow: 'rgba(255, 255, 255, 0.3)' }
};

const getPageTheme = (pathname = '') => {
  const cleanPath = pathname.replace(/^\/en/, '') || '/';
  if (cleanPath === '/' || cleanPath === '') return PAGE_THEMES.home;
  if (cleanPath.startsWith('/secret') || cleanPath.startsWith('/zeroday')) return PAGE_THEMES.zeroday;
  if (cleanPath.startsWith('/rcon')) return PAGE_THEMES.rcon;
  if (cleanPath.startsWith('/ddos')) return PAGE_THEMES.ddos;
  if (cleanPath.startsWith('/bots')) return PAGE_THEMES.bots;
  if (cleanPath.startsWith('/server-data-extraction') || cleanPath.startsWith('/data')) return PAGE_THEMES.data;
  if (cleanPath.startsWith('/account-checker') || cleanPath.startsWith('/checker')) return PAGE_THEMES.checker;
  if (cleanPath.startsWith('/servers-parser') || cleanPath.startsWith('/monitoring-parser')) return PAGE_THEMES.parser;
  if (cleanPath.startsWith('/servers')) return PAGE_THEMES.servers;
  if (cleanPath === '/account' || cleanPath.startsWith('/account/')) return PAGE_THEMES.account;
  return PAGE_THEMES.home;
};

const getFriendlyErrorMessage = (error, lang) => {
  if (!error) return '';
  const isRu = lang === 'ru';
  if (typeof error !== 'string') return String(error);

  const lower = error.toLowerCase();
  if (lower.includes('blacklisted') || lower.includes('реестре защиты')) {
    return isRu ? 'Этот сервер находится в реестре защиты и защищён от тестирования.' : 'This server is protected and cannot be tested.';
  }
  if (lower.includes('offline') || lower.includes('недоступен')) {
    return isRu ? 'Целевой сервер недоступен (оффлайн).' : 'Target server is offline.';
  }
  if (lower.includes('empty')) {
    return isRu ? 'Адрес сервера не может быть пустым.' : 'IP Address cannot be empty.';
  }
  if (lower.includes('log in') || lower.includes('авториз')) {
    return isRu ? 'Пожалуйста, авторизуйтесь для проверки серверов.' : 'Please log in first.';
  }
  if (lower.includes('websocket')) {
    return isRu ? 'Подключение к серверу потеряно. Подождите...' : 'WebSocket not connected. Please wait or refresh.';
  }
  if (lower.includes('invalid server data')) {
    return isRu ? 'Получены некорректные данные о сервере.' : 'Received invalid server data structure.';
  }
  if (lower.includes('превышено время') || lower.includes('timed out')) {
    return isRu ? 'Превышено время ожидания ответа от сервера. Попробуйте еще раз.' : 'Server response timed out. Please try again.';
  }
  return error;
};

const ServerInfoCard = ({ className = "w-full mx-auto relative z-40 mb-8" }) => {
  const location = useLocation();
  const currentTheme = getPageTheme(location.pathname);
  const [isHoveredDisconnect, setIsHoveredDisconnect] = useState(false);
  const [isFormFocused, setIsFormFocused] = useState(false);
  const [isSubmitHovered, setIsSubmitHovered] = useState(false);
  const { language } = useLanguage();
  const { showToast } = useNotification();
  const [inputIpAddress, setInputIpAddress] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isEditingTarget, setIsEditingTarget] = useState(false);

  const {
    serverInfo,
    isConnectedToServer,
    isConnecting,
    connectionError,
    targetIp,
    connectToServer,
    disconnectFromServer
  } = useServerConnection();
  const { isAuthenticated, token, triggerGoogleLogin, isLoggingIn } = useAuth();
  const skipNextFetchRef = useRef(false);
  const containerRef = useRef(null);
  const wasConnectingRef = useRef(false);
  const abortControllerRef = useRef(null);
  const lastErrorRef = useRef(connectionError);
  const knownServersRef = useRef(new Map());
  const queryCacheRef = useRef(new Map());
  const inputIpRef = useRef(inputIpAddress);
  inputIpRef.current = inputIpAddress;

  // Pre-load known recent servers from localStorage into memory pool
  useEffect(() => {
    try {
      const raw = localStorage.getItem('stormhack_recent_servers');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          parsed.forEach((s) => {
            if (s?.ip) {
              knownServersRef.current.set(s.ip.toLowerCase(), {
                ip: s.ip,
                status: s.status || (s.online ? 'Online' : 'Offline'),
                players: s.players !== undefined ? s.players : '',
                version: s.version?.name || s.version || '',
                icon: typeof s.icon === 'string' ? s.icon : null,
                createdAt: s.createdAt || s.timeAgo || null
              });
            }
          });
        }
      }
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    // Only show connection error if we are NOT currently connected to a server
    if (connectionError && !isConnectedToServer && !serverInfo) {
      if (connectionError !== lastErrorRef.current) {
        lastErrorRef.current = connectionError;
        showToast(getFriendlyErrorMessage(connectionError, language), { type: 'error' });
      }
    } else {
      // Keep lastErrorRef aligned so route switches or connected state never re-trigger stale toasts
      lastErrorRef.current = connectionError || null;
    }
  }, [connectionError, isConnectedToServer, serverInfo, language, showToast]);

  const [lastServer, setLastServer] = useState(serverInfo);

  useEffect(() => {
    if (serverInfo) {
      setLastServer(serverInfo);
      setIsEditingTarget(false);
      if (serverInfo.ip) {
        knownServersRef.current.set(serverInfo.ip.toLowerCase(), {
          ip: serverInfo.hostname || serverInfo.ip,
          status: 'Online',
          players: serverInfo.players?.online !== undefined ? `${serverInfo.players.online}/${serverInfo.players.max || 1000}` : '',
          version: serverInfo.version?.name || '',
          icon: serverInfo.icon || null,
          createdAt: 'Сейчас'
        });
      }
    }
  }, [serverInfo]);

  useEffect(() => {
    if (connectionError && !serverInfo) {
      setLastServer(null);
    }
  }, [connectionError, serverInfo]);

  const displayServer = isEditingTarget ? null : (serverInfo || (isConnecting ? lastServer : null));

  useEffect(() => {
    if (isConnecting) {
      wasConnectingRef.current = true;
      setShowSuggestions(false);
      setSuggestions([]);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    } else {
      if (wasConnectingRef.current && serverInfo) {
        playVoice('enter-server');
      }
      wasConnectingRef.current = false;
    }
  }, [isConnecting, serverInfo]);

  useEffect(() => {
    if (!isConnectedToServer && targetIp) {
      setInputIpAddress(targetIp);
      inputIpRef.current = targetIp;
    } else if (!isConnectedToServer && !targetIp) {
      setInputIpAddress('');
      inputIpRef.current = '';
    }
  }, [targetIp, isConnectedToServer]);

  // Synchronous local match function (0ms latency during typing)
  const getInstantLocalMatches = useCallback((query) => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return [];

    if (queryCacheRef.current.has(q)) {
      return queryCacheRef.current.get(q);
    }

    const exactPrefixMatches = [];
    const otherMatches = [];

    for (const [key, s] of knownServersRef.current.entries()) {
      if (key.startsWith(q)) {
        exactPrefixMatches.push(s);
      } else if (key.includes(q)) {
        otherMatches.push(s);
      }
    }

    return [...exactPrefixMatches, ...otherMatches].slice(0, 8);
  }, []);

  const fetchSuggestions = useCallback(async (query) => {
    if (!isAuthenticated || !token || query.length < 2 || isConnecting || isConnectedToServer) {
      return;
    }

    const q = query.trim().toLowerCase();
    if (queryCacheRef.current.has(q)) {
      const cached = queryCacheRef.current.get(q);
      if (cached.length > 0) {
        setSuggestions(cached);
        if (!isConnecting && !isConnectedToServer) {
          setShowSuggestions(true);
        }
      }
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const response = await axios.get(
        `${config.api.baseUrl}/api/server/suggestions?query=${encodeURIComponent(query)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal
        }
      );
      if (response.data?.success && Array.isArray(response.data.data)) {
        const items = response.data.data;
        queryCacheRef.current.set(q, items);
        items.forEach((s) => {
          if (s?.ip) knownServersRef.current.set(s.ip.toLowerCase(), s);
        });

        const currentInput = (inputIpRef.current || '').trim().toLowerCase();
        if (currentInput === q || currentInput.startsWith(q)) {
          const displayItems = currentInput === q
            ? items
            : items.filter(s => s.ip.toLowerCase().includes(currentInput));

          if (displayItems.length > 0) {
            setSuggestions(displayItems);
            if (!isConnecting && !isConnectedToServer) {
              setShowSuggestions(true);
            }
          }
        }
      }
    } catch (error) {
      if (axios.isCancel(error) || error.name === 'CanceledError') {
        return;
      }
      // On network jitter or rate limits, do NOT wipe suggestions or close the dropdown
      console.warn('Failed to fetch suggestions:', error?.message);
    }
  }, [isAuthenticated, token, isConnecting, isConnectedToServer]);

  // Fast debounced suggestions fetch (35ms)
  useEffect(() => {
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }

    if (isConnecting || isConnectedToServer) {
      setShowSuggestions(false);
      setSuggestions([]);
      return;
    }

    const trimmed = inputIpAddress.trim();
    if (trimmed.length < 2) {
      return;
    }

    // If already in queryCache, fetch immediately without delay
    if (queryCacheRef.current.has(trimmed.toLowerCase())) {
      fetchSuggestions(trimmed);
      return;
    }

    const timer = setTimeout(() => {
      fetchSuggestions(trimmed);
    }, 35);

    return () => clearTimeout(timer);
  }, [inputIpAddress, isConnecting, isConnectedToServer, fetchSuggestions]);

  // Close suggestions on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleConnectClick = useCallback((ipOverride) => {
    setIsEditingTarget(false);
    setShowSuggestions(false);
    setSuggestions([]);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    if (!isAuthenticated) { triggerGoogleLogin(); return; }
    const ip = typeof ipOverride === 'string' ? ipOverride : inputIpAddress;
    if (ip?.trim()) {
      skipNextFetchRef.current = true;
      connectToServer(ip);
    }
  }, [isAuthenticated, inputIpAddress, connectToServer, triggerGoogleLogin]);

  const handleDisconnectClick = useCallback(() => {
    setIsEditingTarget(true);
    setLastServer(null);
    disconnectFromServer(true);
    setInputIpAddress('');
  }, [disconnectFromServer]);

  const handleSuggestionClick = useCallback((ip) => {
    setIsEditingTarget(false);
    setShowSuggestions(false);
    setSuggestions([]);
    setInputIpAddress(ip);
    skipNextFetchRef.current = true;
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (!isAuthenticated) { triggerGoogleLogin(); return; }
    if (ip?.trim()) connectToServer(ip);
  }, [isAuthenticated, connectToServer, triggerGoogleLogin]);

  const handleInputFocus = useCallback(() => {
    if (isConnecting || isConnectedToServer) return;
    const trimmed = inputIpAddress.trim();
    if (trimmed.length >= 1) {
      const instant = getInstantLocalMatches(trimmed);
      if (instant.length > 0) {
        setSuggestions(instant);
        setShowSuggestions(true);
      }
      if (trimmed.length >= 2) {
        fetchSuggestions(trimmed);
      }
    }
  }, [inputIpAddress, isConnecting, isConnectedToServer, getInstantLocalMatches, fetchSuggestions]);

  // Highlight matched part of IP
  const highlightMatch = (ip) => {
    const query = inputIpAddress.toLowerCase();
    const idx = ip.toLowerCase().indexOf(query);
    if (idx === -1) return <span>{ip}</span>;
    return (
      <>
        <span className="text-gray-500">{ip.slice(0, idx)}</span>
        <span className="text-white font-semibold">{ip.slice(idx, idx + query.length)}</span>
        <span className="text-gray-500">{ip.slice(idx + query.length)}</span>
      </>
    );
  };

  return (
    <div className={className} ref={containerRef}>
      <AnimatePresence mode="wait" initial={false}>
        {displayServer ? (
          /* ═══════ 2. Лицевая карточка сервера с MOTD (Apple / Grok стиль) ═══════ */
          <motion.div
            key="server-motd-card"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="rounded-[24px] sm:rounded-[28px] p-4 sm:p-6 text-left relative shadow-[0_25px_60px_-10px_rgba(0,0,0,0.95),inset_0_1px_2px_rgba(255,255,255,0.15)] border border-white/[0.2] bg-[#111319]"
          >
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 mb-4 border-b border-white/[0.12]">
              <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                <div className="w-11 h-11 sm:w-[52px] sm:h-[52px] rounded-[12px] sm:rounded-[14px] bg-[#16161d] border border-white/25 flex items-center justify-center shrink-0 overflow-hidden shadow-[0_4px_14px_rgba(0,0,0,0.6)]">
                  {displayServer.icon ? (
                    <img src={displayServer.icon} className="w-full h-full object-cover" alt="" />
                  ) : (
                    <svg viewBox="0 0 24 24" className="w-6 h-6 sm:w-7 sm:h-7 fill-white">
                      <path d="M4 4h4v6h8V4h4v16h-4v-6H8v6H4z" />
                    </svg>
                  )}
                </div>
                <div className="min-w-0">
                  <span className="font-mono text-base sm:text-[1.18rem] font-bold text-white tracking-tight block truncate">
                    {displayServer.hostname || displayServer.ip}
                  </span>
                  <div className="flex items-center gap-2 text-[11px] sm:text-xs text-white/65 mt-0.5 flex-wrap">
                    <span className="inline-flex items-center gap-1.5 text-emerald-400 font-bold">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_6px_#2ed573]"></span>
                      ONLINE
                    </span>
                    <span>•</span>
                    <span className="text-white/80 font-mono">{displayServer.ping || 24}ms</span>
                    <span>•</span>
                    <span className="text-white/80 truncate max-w-[150px] sm:max-w-none">
                      {displayServer.version?.name || 'Minecraft 1.8.x – 1.21.x'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Кнопка смены цели / Подключение */}
              <button
                onClick={handleDisconnectClick}
                disabled={isConnecting}
                onMouseEnter={() => setIsHoveredDisconnect(true)}
                onMouseLeave={() => setIsHoveredDisconnect(false)}
                style={{
                  backgroundColor: isHoveredDisconnect ? currentTheme.hoverBg : currentTheme.bg,
                  color: '#000000',
                  boxShadow: isHoveredDisconnect ? `0 2px 14px ${currentTheme.shadow}` : 'none'
                }}
                className={`inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all duration-200 shrink-0 cursor-pointer !text-black w-full sm:w-auto ${
                  isConnecting ? 'opacity-90 pointer-events-none' : 'hover:scale-[1.02] active:scale-[0.98]'
                }`}
              >
                {isConnecting ? (
                  <>
                    <span className="w-3.5 h-3.5 border-2 border-black/20 border-t-black rounded-full animate-spin shrink-0" />
                    <span className="!text-black">{language === 'en' ? 'Connecting...' : 'Подключение...'}</span>
                  </>
                ) : (
                  <>
                    <span className="!text-black">{language === 'en' ? 'Change Target' : 'Сменить цель'}</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="!stroke-black" stroke="#000000" strokeWidth="2.5">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </>
                )}
              </button>
            </div>

            {/* Minecraft MOTD окно */}
            <div className="bg-[#020204] border border-white/[0.16] rounded-[16px] sm:rounded-[18px] p-3 sm:p-[14px_18px] font-mono text-xs sm:text-[0.95rem] leading-relaxed text-white mb-4 shadow-[inset_0_2px_8px_rgba(0,0,0,0.8)] select-text break-words">
              {parseMinecraftFormatting(displayServer.motd?.raw || displayServer.motd?.clean || displayServer.motd?.text || '✔ Добро пожаловать на сервер • Соединение установлено')}
            </div>

            {/* Метрики в футере */}
            <div className="flex items-center gap-x-3.5 gap-y-1.5 text-xs sm:text-sm text-white/65 flex-wrap pt-1 font-mono">
              <div className="inline-flex items-center gap-1.5">
                <span className="text-white/50 font-sans">Игроков онлайн:</span>
                <strong className="text-white font-semibold">
                  {displayServer.players?.online !== undefined
                    ? `${displayServer.players.online} / ${displayServer.players.max || 1000}`
                    : '48,290 / 100,000'}
                </strong>
              </div>
              <span className="hidden sm:inline text-white/30">•</span>
              <div className="inline-flex items-center gap-1.5">
                <span className="text-white/50 font-sans">Ядро:</span>
                <strong className="text-white font-semibold">
                  {displayServer.software || 'BungeeCord / Paper'}
                </strong>
              </div>
              <span className="hidden sm:inline text-white/30">•</span>
              <div className="inline-flex items-center gap-1.5">
                <span className="text-white/50 font-sans">Протокол:</span>
                <strong className="text-white font-semibold">
                  v{displayServer.protocol || '765'}
                </strong>
              </div>
            </div>
          </motion.div>
        ) : (
          /* ═══════ 1. Форма ввода (исходное состояние) ═══════ */
          <motion.div
            key="ip-input-form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="relative"
          >
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleConnectClick();
              }}
              onFocus={() => setIsFormFocused(true)}
              onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) {
                  setIsFormFocused(false);
                }
              }}
              style={{
                borderColor: isFormFocused ? currentTheme.bg : 'rgba(255, 255, 255, 0.14)',
                backgroundColor: isFormFocused ? '#0d0f15' : '#111319',
                boxShadow: isFormFocused
                  ? `0 20px 50px -10px rgba(0,0,0,0.95), 0 0 0 1px ${currentTheme.bg}, 0 0 24px ${currentTheme.shadow}`
                  : '0 20px 45px -10px rgba(0,0,0,0.85), inset 0 1px 1px rgba(255,255,255,0.08)'
              }}
              className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 p-2 sm:p-2 sm:pl-6 rounded-[22px] sm:rounded-full border transition-all duration-200"
            >
              <div className="flex items-center w-full min-w-0 flex-1">
                <div className="hidden sm:flex items-center text-white/60 shrink-0 mr-2">
                  <svg viewBox="0 0 24 24" className="w-5 h-5 stroke-current fill-none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
                    <path d="M2 12h20" />
                  </svg>
                </div>

                <input
                  type="text"
                  disabled={isConnecting}
                  className={`w-full bg-transparent border-0 ring-0 focus:ring-0 text-[1rem] sm:text-[1.1rem] font-medium text-white placeholder-white/45 tracking-[0.01em] focus:outline-none px-3 py-2 sm:py-1 ${
                    isConnecting ? 'opacity-60 cursor-not-allowed select-none' : ''
                  }`}
                  placeholder={language === 'en' ? 'Server IP:PORT (e.g. mc.hypixel.net:25565)' : 'IP:PORT целевого сервера (например, mc.hypixel.net:25565)'}
                  value={inputIpAddress}
                  onChange={(e) => {
                    if (isConnecting) return;
                    skipNextFetchRef.current = false;
                    const val = e.target.value;
                    setInputIpAddress(val);
                    inputIpRef.current = val;
                    const trimmed = val.trim();
                    if (!trimmed) {
                      setShowSuggestions(false);
                      setSuggestions([]);
                      return;
                    }
                    // Instant synchronous match in the current frame (0ms latency!)
                    const instantMatches = getInstantLocalMatches(trimmed);
                    if (instantMatches.length > 0) {
                      setSuggestions(instantMatches);
                      setShowSuggestions(true);
                    }
                  }}
                  onFocus={handleInputFocus}
                  autoComplete="off"
                />

                {inputIpAddress && !isConnecting && (
                  <button
                    type="button"
                    onClick={() => {
                      setInputIpAddress('');
                      setShowSuggestions(false);
                      setSuggestions([]);
                    }}
                    className="text-white/50 hover:text-white p-1.5 shrink-0 transition-colors mr-1 cursor-pointer"
                    aria-label="Очистить"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                )}
              </div>

              <button
                type="submit"
                disabled={isConnecting}
                onMouseEnter={() => setIsSubmitHovered(true)}
                onMouseLeave={() => setIsSubmitHovered(false)}
                style={{
                  backgroundColor: isSubmitHovered ? currentTheme.hoverBg : currentTheme.bg,
                  color: '#000000',
                  boxShadow: isSubmitHovered ? `0 4px 20px ${currentTheme.shadow}` : 'none'
                }}
                className={`w-full sm:w-auto font-bold text-sm sm:text-[0.98rem] px-7 py-3.5 rounded-full transition-all duration-200 hover:scale-[1.01] active:scale-[0.98] flex items-center justify-center gap-2 shrink-0 cursor-pointer !text-black ${
                  isConnecting || (!isAuthenticated && isLoggingIn) ? 'opacity-90 pointer-events-none' : ''
                }`}
              >
                {isConnecting ? (
                  <>
                    <span className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin shrink-0" />
                    <span className="!text-black">Подключение...</span>
                  </>
                ) : (!isAuthenticated && isLoggingIn) ? (
                  <>
                    <span className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin shrink-0" />
                    <span className="!text-black">{language === 'en' ? 'Signing in...' : 'Авторизация...'}</span>
                  </>
                ) : (
                  <>
                    <span className="!text-black">Начать аудит</span>
                    <svg viewBox="0 0 24 24" className="w-4 h-4 !stroke-black fill-none" stroke="#000000" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </>
                )}
              </button>
            </form>

            {/* Modern Apple / Grok Suggestions Dropdown */}
            <AnimatePresence>
              {showSuggestions && !isConnecting && suggestions.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -4, scale: 0.99 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -3, scale: 0.99 }}
                  transition={{ duration: 0.12, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute top-full left-0 right-0 mt-2 bg-[#0c0d14]/95 border border-white/[0.16] rounded-2xl shadow-[0_20px_50px_-10px_rgba(0,0,0,0.95),inset_0_1px_1px_rgba(255,255,255,0.15)] overflow-hidden z-[100] backdrop-blur-3xl text-left"
                >
                  <div className="max-h-[320px] overflow-y-auto divide-y divide-white/[0.06]">
                    {suggestions.map((s, i) => {
                      const isOnline = s.status === 'Online' || s.online === true;
                      return (
                        <div
                          key={s.ip || i}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleSuggestionClick(s.ip);
                          }}
                          className="group w-full px-4 py-2.5 hover:bg-white/[0.10] cursor-pointer flex items-center justify-between first:rounded-t-2xl last:rounded-b-2xl select-none"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-7 h-7 rounded-lg bg-white/[0.06] border border-white/[0.12] flex items-center justify-center shrink-0 overflow-hidden text-white/70 group-hover:text-white group-hover:border-white/25">
                              {s.icon ? (
                                <img src={s.icon} className="w-full h-full object-cover" alt="" />
                              ) : (
                                <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current">
                                  <path d="M4 4h4v6h8V4h4v16h-4v-6H8v6H4z" />
                                </svg>
                              )}
                            </div>
                            <div className="min-w-0">
                              <span className="text-[13.5px] font-mono font-medium truncate text-white/90 group-hover:text-white tracking-tight block">
                                {highlightMatch(s.ip)}
                              </span>
                              {s.version && (
                                <div className="text-[11px] text-white/45 truncate font-mono leading-none mt-1">
                                  {s.version}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-2.5 shrink-0 ml-2 font-mono text-xs text-white/50">
                            {s.players !== undefined && (
                              <span className="inline-flex items-center gap-1 text-[11px] bg-white/[0.04] px-1.5 py-0.5 rounded-md border border-white/5">
                                <svg viewBox="0 0 24 24" className="w-3 h-3 stroke-current fill-none" strokeWidth="2">
                                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                  <circle cx="9" cy="7" r="4" />
                                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                </svg>
                                {s.players}
                              </span>
                            )}
                            {s.createdAt && (
                              <span className="hidden sm:inline text-[11px] text-white/40">
                                {formatTimeAgo(s.createdAt, language)}
                              </span>
                            )}
                            <div className="w-5 h-5 rounded-md bg-white/0 group-hover:bg-white/10 flex items-center justify-center">
                              <svg viewBox="0 0 24 24" className="w-3 h-3 stroke-white/40 group-hover:stroke-white group-hover:translate-x-0.5" fill="none" strokeWidth="2.5">
                                <path d="M5 12h14M12 5l7 7-7 7" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>


          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ServerInfoCard;